import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Cpu, Eye, Trophy, ShieldCheck, Building2, BadgeCheck } from "lucide-react";
import { listTeam } from "@/lib/api";
import Seo, { ORG_JSONLD, breadcrumbJsonLd } from "@/components/Seo";

const PHILOSOPHY = [
  {
    icon: Cpu,
    title: "L'AI supporta. Le persone decidono.",
    body: "Ogni sistema di intelligenza artificiale usato nel recruiting è classificato dall'AI Act europeo come a basso rischio.\n\nLovac costruisce i propri processi in conformità con questa normativa: ogni decisione su un candidato, chi contattare, chi presentare, chi raccomandare, è sempre presa da un professionista Lovac.\n\nL'AI ci permette di lavorare meglio e più velocemente. Non di lavorare al posto nostro.",
  },
  {
    icon: Eye,
    title: "Trasparenza reale.",
    body: "Non devi rincorrerci per avere aggiornamenti.\n\nIl cliente vede la ricerca in tempo reale, quante figure contattate, chi ha risposto, chi è stato valutato, chi è pronto per essere presentato.\n\nSapere cosa sta succedendo non è un optional. È il minimo che ci si aspetta da un partner serio.",
  },
  {
    icon: Trophy,
    title: "Risultati, non processo.",
    body: "Non fatturiamo ore. Non vendiamo report. Lavoriamo a success fee: si paga quando c'è un risultato concreto, una persona inserita, che funziona, che resta.\n\nSe non portiamo candidati in linea con quello che cerchi, l'acconto viene rimborsato. Senza discussioni.",
  },
];

export default function ChiSiamo() {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    listTeam().then(setTeam).catch(() => setTeam([]));
  }, []);

  return (
    <>
      <Seo
        title="Chi siamo, Lovac, agenzia di head hunting tech-driven | Lovac"
        description="Lovac è un'agenzia di recruiting italiana che lavora come una tech company: database di 10M+ profili, AI, recruiter senior. +500 figure reclutate, +300 aziende aiutate."
        path="/chi-siamo"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "AboutPage", url: "https://lovac.it/chi-siamo", inLanguage: "it-IT", name: "Chi siamo, Lovac" },
            ORG_JSONLD,
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Chi siamo", path: "/chi-siamo" },
            ]),
          ],
        }}
      />
      {/* HERO */}
      <section className="bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-lovac px-6 pt-24 pb-20 relative z-10">
          <span className="badge-on-dark">Chi siamo</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 text-on-dark max-w-4xl leading-[1.05]">
            Siamo la tech company<br />
            <span className="text-lovac-accent">che il recruiting italiano aspettava.</span>
          </h1>
          <p className="text-on-dark-muted text-lg mt-6 max-w-2xl">
            Costruiti per trovare le persone giuste, non per passarti CV.
          </p>
        </div>
      </section>

      {/* CHI SIAMO */}
      <section className="py-24 bg-lovac">
        <div className="container-lovac px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-6 text-lg text-lovac-dark leading-[1.75]">
              <p className="font-display text-2xl text-lovac-dark">
                Lovac è un'agenzia di recruiting italiana che si comporta come una tech company.
              </p>
              <p className="text-lovac-muted">
                Usiamo un database di 10 milioni di figure professionali verificate, strumenti di intelligenza artificiale per analizzare i profili e un portale proprietario che tiene il cliente aggiornato in tempo reale. Ma al centro del metodo ci sono sempre i recruiter, persone reali, raggiungibili, che conoscono la tua ricerca nel dettaglio.
              </p>
              <p>
                Il risultato: <span className="text-lovac-accent font-medium">oltre 500 figure reclutate</span>, più di <span className="text-lovac-accent font-medium">300 aziende aiutate</span>, un time to hire medio sotto i <span className="text-lovac-accent font-medium">30 giorni</span>.
              </p>
            </div>
            <div className="relative">
              <img
                src="/images/chi-siamo-team.jpg"
                alt="Sede Lovac, vista aerea"
                className="w-full h-auto rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.12)]"
                loading="lazy"
                data-testid="chi-siamo-photo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-24 bg-lovac-2">
        <div className="container-lovac">
          <div className="max-w-3xl">
            <p className="badge-lovac">Il team</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark leading-tight">
              Le persone dietro ogni ricerca.
            </h2>
            <p className="text-lovac-muted mt-5 text-base max-w-2xl leading-relaxed">
              Oltre 30 professionisti del recruiting, ognuno con un focus preciso sul proprio segmento. Persone che conoscono il mercato e sanno come muoversi dentro.
            </p>
          </div>

          {team.length === 0 ? (
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-square rounded-2xl bg-lovac border-lovac opacity-50 animate-pulse" />
              ))}
              <p className="lg:col-span-4 text-sm text-lovac-muted italic text-center mt-2">
                I membri del team verranno pubblicati a breve.
              </p>
            </div>
          ) : (
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {team.map((m) => (
                <div key={m.id} className="group">
                  <div
                    className="aspect-square rounded-2xl overflow-hidden relative"
                    style={{
                      background:
                        "linear-gradient(160deg, var(--lovac-accent) 0%, var(--lovac-accent-hover) 100%)",
                    }}
                  >
                    <img
                      src={m.foto_url}
                      alt={m.nome}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <p className="font-display text-lg text-lovac-dark mt-4 leading-tight">{m.nome}</p>
                  <p className="text-sm text-lovac-muted mt-1">{m.ruolo}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* GRUPPO */}
      <section className="py-24 bg-lovac">
        <div className="container-lovac grid lg:grid-cols-[1fr_1.4fr] gap-14 items-start">
          <div>
            <p className="badge-lovac">Il gruppo</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark leading-tight">
              Parte di qualcosa di più grande.
            </h2>
          </div>
          <div className="space-y-5 text-base text-lovac-dark leading-relaxed max-w-2xl">
            <p>
              Lovac è il brand principale di un gruppo che opera nello sviluppo e nella crescita delle PMI italiane.
            </p>
            <p className="text-lovac-muted">
              Il gruppo include anche i brand <span className="text-lovac-dark font-medium">Sales Hunter</span>, specializzata nel recruiting commerciale, e <span className="text-lovac-dark font-medium">Venditore a Distanza</span>, la prima sales agency italiana specializzata nell'outsourcing del reparto commerciale da remoto.
            </p>
            <p className="font-display text-xl text-lovac-dark">
              Due approcci diversi allo stesso problema: aiutare le aziende italiane a crescere con le persone giuste al posto giusto.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {[
                { name: "Sales Hunter", desc: "Recruiting commerciale verticale" },
                { name: "Venditore a Distanza", desc: "Outsourcing reparto commerciale remoto" },
              ].map((b) => (
                <div key={b.name} className="card-lovac">
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 size={16} className="text-lovac-accent" />
                    <p className="font-display text-base text-lovac-dark">{b.name}</p>
                  </div>
                  <p className="text-sm text-lovac-muted">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FILOSOFIA */}
      <section className="py-24 bg-lovac-2">
        <div className="container-lovac">
          <div className="max-w-3xl">
            <p className="badge-lovac">Filosofia</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark leading-tight">
              Come pensiamo al recruiting.
            </h2>
          </div>

          <div className="mt-14 grid lg:grid-cols-3 gap-6">
            {PHILOSOPHY.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="rounded-xl p-8 flex flex-col h-full"
                  style={{ background: "var(--lovac-bg)", border: "0.5px solid var(--lovac-border)" }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="h-10 w-10 rounded-lg flex items-center justify-center"
                      style={{ background: "rgba(0,196,140,0.12)" }}>
                      <Icon size={18} className="text-lovac-accent" />
                    </div>
                    <span className="font-mono text-xs text-lovac-muted">0{i + 1}</span>
                  </div>
                  <h3 className="font-display text-xl text-lovac-dark leading-snug">{p.title}</h3>
                  <p className="text-sm text-lovac-muted mt-4 leading-relaxed whitespace-pre-line">
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section className="py-24 bg-lovac">
        <div className="container-lovac grid lg:grid-cols-[1fr_1.4fr] gap-14 items-start">
          <div>
            <p className="badge-lovac">Compliance</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark leading-tight">
              Lavoriamo nel rispetto delle regole.<br />
              <span className="text-lovac-muted">Anche quelle che gli altri ignorano.</span>
            </h2>
          </div>
          <div className="space-y-5 text-base text-lovac-dark leading-relaxed max-w-2xl">
            <p>
              Lovac è conforme all'<span className="font-medium">AI Act europeo</span>. Usiamo l'AI per accelerare il lavoro dei recruiter, analisi dei profili, schede candidato, report di mercato, ma ogni decisione su un candidato è sempre presa da un professionista Lovac.
            </p>
            <p className="text-lovac-muted">
              Siamo conformi alle nuove regole europee di <span className="text-lovac-dark">trasparenza salariale</span> e al <span className="text-lovac-dark">GDPR</span> nel trattamento dei dati dei candidati.
            </p>
            <p className="text-lovac-muted">
              Ogni candidato che entra nel nostro processo viene informato dell'uso di strumenti digitali e ha il diritto di parlare direttamente con un recruiter Lovac in qualsiasi momento.
            </p>
            <p className="font-display text-xl text-lovac-dark">
              Non è solo un obbligo. È il modo in cui pensiamo che si debba lavorare con le persone.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {[
                { i: ShieldCheck, t: "AI Act compliant" },
                { i: BadgeCheck, t: "GDPR + Trasparenza salariale UE" },
              ].map((c) => {
                const Icon = c.i;
                return (
                  <div key={c.t} className="flex items-center gap-3 card-lovac py-3">
                    <Icon size={18} className="text-lovac-accent shrink-0" />
                    <p className="text-sm text-lovac-dark font-medium">{c.t}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="py-24 bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-narrow text-center relative z-10">
          <span className="badge-on-dark">Prenota una call</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-6 text-on-dark leading-tight">
            Vuoi sapere come lavoriamo<br />sulla tua ricerca?
          </h2>
          <p className="text-on-dark-muted mt-5 max-w-xl mx-auto">
            Prenota una call di 30 minuti. Ti raccontiamo il metodo nel dettaglio e vediamo se facciamo al caso tuo.
          </p>
          <Link to="/contatti" className="btn-primary mt-10 inline-flex">
            Prenota una call <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
}
